/**
 * Organization Domain Utility
 * Provides utilities for looking up organization domains by orgId
 */

import * as organizationService from '../domain/services/organizationService';

// Cache for organization domains (orgId -> domain)
const domainCache = new Map();
let cacheInitialized = false;

/**
 * Get domain for a given orgId
 * @param {string} orgId - Organization ID
 * @returns {Promise<string|null>} Domain name or orgId if not found
 */
export async function getOrgDomain(orgId) {
  if (!orgId) {
    return null;
  }

  // Check cache first
  if (domainCache.has(orgId)) {
    return domainCache.get(orgId);
  }

  try {
    const org = await organizationService.getOrganizationById(orgId);
    const domain = org ? (org.domain || org.name || orgId) : orgId;
    
    // Cache the result
    domainCache.set(orgId, domain);
    
    return domain;
  } catch (error) {
    console.error('Error fetching org domain:', error);
    return orgId; // Fallback to orgId if lookup fails
  }
}

/**
 * Get all organizations with their domains
 * @returns {Promise<Array>} Array of { orgId, domain } objects
 */
export async function getAllOrgDomains() {
  try {
    const orgs = await organizationService.getAllOrganizations();
    
    const orgDomains = orgs.map(org => ({
      orgId: org.id,
      domain: org.domain || org.name || org.id
    }));
    
    // Update cache
    orgDomains.forEach(({ orgId, domain }) => {
      domainCache.set(orgId, domain);
    });
    
    cacheInitialized = true;
    
    return orgDomains;
  } catch (error) {
    console.error('Error fetching all org domains:', error);
    return [];
  }
}

/**
 * Clear the domain cache (useful for testing or when orgs are updated)
 */
export function clearCache() {
  domainCache.clear();
  cacheInitialized = false;
}

/**
 * Initialize cache by loading all organizations
 */
export async function initializeCache() {
  if (!cacheInitialized) {
    await getAllOrgDomains();
  }
}
